import React from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, Typography, Paper, Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import SortableQueueItem from './SortableQueueItem';

/**
 * Queue Tab - Drag-and-drop queue management
 */
const QueueTab = ({ questions, onQueueReorder, onSendNow, onRemove, getNextSunday }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter and sort queued questions
  const queuedQuestions = questions
    .filter(q => q.status === 'queued' && q.queueOrder)
    .sort((a, b) => a.queueOrder - b.queueOrder);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = queuedQuestions.findIndex(q => q.id === active.id);
      const newIndex = queuedQuestions.findIndex(q => q.id === over.id);

      const reordered = arrayMove(queuedQuestions, oldIndex, newIndex);
      onQueueReorder(reordered);
    }
  };

  const nextSunday = getNextSunday();

  return (
    <Box>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: 'primary.light' }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          📅 Question Queue
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Next sends: {format(nextSunday, 'EEEE, MMMM d @ h:mm a')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Drag to reorder • Position 1 sends next Sunday
        </Typography>
      </Paper>

      {/* Queue List */}
      {queuedQuestions.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="body1" color="text.secondary">
            No questions in queue. Promote suggestions to get started!
          </Typography>
        </Paper>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={queuedQuestions.map(q => q.id)}
            strategy={verticalListSortingStrategy}
          >
            {queuedQuestions.map((question, index) => (
              <SortableQueueItem
                key={question.id}
                question={question}
                index={index}
                onSendNow={onSendNow}
                onRemove={onRemove}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </Box>
  );
};

export default QueueTab;
